import { t } from '@ctcells/i18n'

import type { TabItem } from '../../layout/Tabs.tsx'

import { TRAY_LABEL_KEY, type MonitoringDeviceKey } from './monitoringModel.ts'

/**
 * 콘솔 「장비」 탭에 그림 블록을 **어떤 순서로 놓을지** 정하는 순수 모델.
 *
 * eric 원문(2026-09-22): **「각각의 모듈에서 작업 후 트레이를 맨 오른쪽에 배치시켜줘」**
 *
 * ```
 * 전:  [트레이(작업 전 | 작업 후)]  [원심분리기]
 * 후:  [C 트레이 · 작업 전]  [원심분리기]  [A 트레이 · 작업 후]
 * ```
 *
 * 🔴 **그림은 한 픽셀도 바꾸지 않는다 — 순서만이다.** 트레이 두 장은 종전에 `figpair` 한 덩이로
 *    붙어 있던 것을 **블록 둘로 가른** 것이고, 그리는 노드는 `FocusFigure` 의 `preTray`/`postTray`
 *    그대로다.
 *
 * 🔴 **여기서 순서를 정하는 까닭** — `FocusFigure` 안에서 가르면 그 파일을 쓰는 장비 관점 콘솔 ·
 *    태블릿 · 모듈 패드 3종의 「전체」 보기와 「트레이」 탭까지 같이 바뀐다. 이 화면
 *    (`EquipmentSheet`)은 **통합 콘솔 전용**이라(`layout='sampleTabs'` 가지 · 시험이 잠갔다)
 *    여기가 가장 작은 자리다.
 *
 * 🔴 **장비 이름으로 분기하지 않는다.** 「트레이 쌍 블록이 있으면 가른다」 한 규칙이고,
 *    염색기처럼 쌍이 없는 장비(`tray` 한 장 + 셰이커 — §16-2)는 **손대지 않은 채 지나간다.**
 *    새 장비가 들어와도 이 함수를 고칠 일이 없다.
 */

/** 트레이 두 장이 한 덩이로 붙어 오던 블록 키(= `figureTabs` 의 탭 키) */
export const TRAY_PAIR = 'trays'
/** 가른 뒤 앞자리 — 작업 «전» 트레이 한 장 */
export const TRAY_PRE = 'trayPre'
/** 가른 뒤 **맨 오른쪽** — 작업 «후» 트레이 한 장 */
export const TRAY_POST = 'trayPost'

/** 트레이가 한 장뿐인 장비(염색기 — §16-2)의 블록 키(= `figureTabs` 의 탭 키) */
export const TRAY_SINGLE = 'tray'

/**
 * 트레이 블록이 붙을 판의 모서리 — **장비가 판 가운데, 트레이는 위 모서리에 작게**
 * (eric 2026-09-29 「중심에 원심분리기 … 왼쪽과 오른쪽 트레이는 작게 왼쪽 상단 오른쪽 상단에」).
 *
 * 🔴 **장비 이름으로 분기하지 않는다.** 블록 키 하나로 정한다 — 작업 전(`trayPre`)과 한 장짜리(`tray`)는
 *    왼쪽 위, 작업 후(`trayPost`)는 오른쪽 위. 트레이가 아닌 블록은 `undefined`(= 판 가운데).
 * ⚠ 염색기는 트레이가 한 장이라 오른쪽 위가 빈다 — 트레이를 두 번 그리지 않는다(§16-2).
 */
export function trayCorner(id: string): 'start' | 'end' | undefined {
  if (id === TRAY_PRE || id === TRAY_SINGLE) return 'start'
  if (id === TRAY_POST) return 'end'
  return undefined
}

/**
 * 블록 한 장. `TabItem` 과 같은 꼴이라 `FocusFigure` 에 `component={id}` 로 그대로 넘어간다.
 */
export type EquipmentBlock = TabItem

/**
 * 「작업 전 트레이 | 장비 본체 | 작업 후 트레이」 순으로 늘어놓는다.
 *
 * @param deviceKey 트레이 이름을 고르는 자(`TRAY_LABEL_KEY`) — 원심은 C/A, 어셈블러는 A/L
 * @param devices `figureTabs(장비)` 에서 「전체」와 「비전」을 뺀 목록(= 왼쪽 열에 설 것들)
 */
export function equipmentBlocks(
  deviceKey: MonitoringDeviceKey,
  devices: readonly TabItem[],
): EquipmentBlock[] {
  const pair = devices.find((block) => block.id === TRAY_PAIR)
  // 🔴 쌍이 없으면 **그대로 돌려준다** — 염색기·6축은 종전 순서 한 칸도 안 바뀐다
  if (pair === undefined) return [...devices]

  const labels = TRAY_LABEL_KEY[deviceKey]
  return [
    { id: TRAY_PRE, label: trayStageLabel(labels.pre, 'monitoring.legend.before') },
    ...devices.filter((block) => block.id !== TRAY_PAIR),
    { id: TRAY_POST, label: trayStageLabel(labels.post, 'monitoring.legend.after') },
  ]
}

/**
 * 「C 트레이 · 작업 전」. 🔴 **위치 이름을 고치지 않는다** — `TRAY_LABEL_KEY` 값은 표시용이
 * 아니라 서버 `position` 문자열과 대조하는 프로토콜 값이라(`ko.ts` 머리말) 보일 때만 합친다.
 */
function trayStageLabel(
  trayKey: (typeof TRAY_LABEL_KEY)[MonitoringDeviceKey]['pre'],
  stageKey: 'monitoring.legend.before' | 'monitoring.legend.after',
): string {
  return t('monitoring.tray.stage', { tray: t(trayKey), stage: t(stageKey) })
}
